<script lang="ts">
	import type { BrandConfig } from '$lib/brand/types';
        import HeaderBrand from './HeaderBrand.svelte';
        import { active } from '$lib/actions/active';
        import { onMount } from 'svelte';
        import { headerVisible, isLandingPage } from '$lib/stores/headerVisibility';
        import { page } from '$app/state';
        import { afterNavigate } from '$app/navigation';

        interface Props {
                config: BrandConfig;
        }

        let { config }: Props = $props();
        let hasScrolled = $state(false);

        const updateHeaderVisibility = () => {
                const scrollY = window.scrollY;
                const currentPath = page.url.pathname;
                const isLanding = currentPath === '/landing/';

                // Update landing page status
                isLandingPage.set(isLanding);

                if (isLanding) {
                        // Show header when scrolled down 100px on landing page
                        if (scrollY > 100) {
                                headerVisible.set(true);
                                hasScrolled = true;
                        } else if (hasScrolled) {
                                // Keep visible once shown, even when scrolling back up
                                headerVisible.set(true);
                        } else {
                                headerVisible.set(false);
                        }
                } else {
                        // Always visible on other pages
                        headerVisible.set(true);
                        hasScrolled = false; // Reset scroll state for non-landing pages
                }
        };

        // Set initial values immediately before component mounts
        const currentPath = page.url.pathname;
        const isLanding = currentPath === '/landing/';
        isLandingPage.set(isLanding);
        headerVisible.set(!isLanding); // Hidden on landing, visible on other pages

        onMount(() => {
                // Handle scroll events
                window.addEventListener('scroll', updateHeaderVisibility);

                // Handle navigation changes
                afterNavigate(() => {
                        hasScrolled = false; // Reset scroll state on navigation
                        updateHeaderVisibility();
                });

                return () => window.removeEventListener('scroll', updateHeaderVisibility);
        });
</script>

<header class="header" class:hidden={!$headerVisible}>
        <HeaderBrand {config} />

        <nav class="nav">
                <a href="/" use:active>Home</a>
                <a href="/about/" use:active>About</a>
                {#if config.product}
                        <a href="/product/" use:active>{config.product.shortname || 'Product'}</a>
                {/if}
                <a href="/contact/" use:active>Contact</a>
        </nav>
</header>

<style>
        .header {
                /* Sticky header positioning */
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 1000;
                /* Production styling preserved */
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                padding: 1rem 2rem;
                background-color: var(--color--primary, var(--brand-primary));
                color: var(--color--text-inverse, white);
                border-bottom: 2px solid var(--color--secondary, var(--brand-secondary));
                /* Glassmorphism effect for overlay */
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                /* Semi-transparent background */
                background-color: color-mix(in srgb, var(--color--primary, var(--brand-primary)) 90%, transparent);
                transition: opacity 0.3s ease, transform 0.3s ease, background-color 0.3s ease;
        }

        .hidden {
                opacity: 0;
                transform: translateY(-100%);
                pointer-events: none;
        }

        /* Solid background on hover for better visibility */
        .header:hover {
                background-color: var(--color--primary, var(--brand-primary));
        }

        .nav {
                display: flex;
                gap: 1.5rem;
        }

        nav a {
                color: white;
                text-decoration: none;
                font-weight: 500;
                transition: color 0.2s ease;
        }

        nav a:hover {
                color: var(--color--accent, var(--color--secondary, var(--brand-secondary)));
        }

        :global(nav a.active) {
                color: var(--color--accent, white) !important;
                text-decoration: underline !important;
                text-decoration-thickness: 2px !important;
                text-underline-offset: 4px !important;
        }

        @media (max-width: 640px) {
                .header {
                        flex-direction: column;
                        align-items: flex-start;
                }

                .nav {
                        width: 100%;
                        justify-content: flex-start;
                        flex-wrap: wrap;
                        gap: 1rem;
                }
        }
</style>